/**
 * "Confirm it's you, then carry on": the one way a change that needs a recent
 * confirmation is sent, on the website and in the apps (social sign-in PRD,
 * D6).
 *
 * Connecting or disconnecting a provider, a first password, asking to change
 * the email and setting two-factor up answer `CONFIRMATION_REQUIRED` (423)
 * without a confirmation from the last ten minutes. `withConfirmation` sends
 * the change, and on a 423 asks the owner to confirm (each platform brings its
 * own UI, as `confirm`) and sends it once more. A second 423 is an error, never
 * a loop.
 *
 * The change is a function, not a request, so every run builds what it sends
 * afresh. A change that spends a provider proof MUST build that proof inside
 * it: a nonce works once, so a proof is never sent twice.
 */

import { accountSecurityService } from './account-security.service';
import { isConfirmationRequiredError } from '../types/api/errors';

import type { ConfirmationDetails } from '../types/api/errors';
import type { IdentityConfirmation } from '../types/auth';

/**
 * Ask the owner to confirm it's them, with one of `confirmation.methods`.
 * Resolves true once they have, false if they backed out.
 */
export type ConfirmIdentity = (
  confirmation: ConfirmationDetails
) => Promise<boolean>;

/** What a change sent through `withConfirmation` came to. */
export type ConfirmationOutcome<T> =
  { status: 'done'; value: T } | { status: 'cancelled' };

export interface WithConfirmationOptions {
  /**
   * Confirm before starting, when the change shouldn't simply be sent again:
   * one that spends a provider proof (the provider's sheet would show twice),
   * or one that leaves the page. Reads the confirmation first and asks up
   * front when it has lapsed.
   */
  precheck?: boolean;
  /** How to read the confirmation. The account security service's, normally. */
  getConfirmation?: () => Promise<IdentityConfirmation>;
}

function detailsOf(state: IdentityConfirmation): ConfirmationDetails {
  return { methods: state.methods, twoFactor: state.two_factor };
}

/**
 * Send `change`, confirming it's the owner when the API asks. Resolves with
 * the change's value, or `cancelled` when the owner backed out of confirming.
 * Anything else the change throws is thrown.
 */
export async function withConfirmation<T>(
  change: () => Promise<T>,
  confirm: ConfirmIdentity,
  options: WithConfirmationOptions = {}
): Promise<ConfirmationOutcome<T>> {
  const getConfirmation =
    options.getConfirmation ??
    ((): Promise<IdentityConfirmation> =>
      accountSecurityService.getConfirmation());

  if (options.precheck) {
    const state = await getConfirmation();
    if (!state.confirmed_until && !(await confirm(detailsOf(state)))) {
      return { status: 'cancelled' };
    }
  }

  try {
    return { status: 'done', value: await change() };
  } catch (error) {
    if (!isConfirmationRequiredError(error)) throw error;

    // The 423 names the ways to confirm; if it didn't, ask for them.
    const confirmation =
      error.confirmation ?? detailsOf(await getConfirmation());
    if (!(await confirm(confirmation))) return { status: 'cancelled' };
  }

  // Once more, and only once: a 423 now is an error, not a loop.
  return { status: 'done', value: await change() };
}
